import { FC } from 'react';
import Page, { PageProps } from '@/components/page';
import { Stack } from '@mui/material';
import DataTable from '@/components/data-table';
import PageHeader from '@/components/page/header';
import { AdminRoleLabels } from '@/lib/models/admin-user.model';
import Text from '@/components/text';
import Button from '@/components/button';
import { DtColumn, DtFilter, DtPage } from '@/components/data-table/helpers';
import { getAdminUsers } from '@/lib/services/admin-user.service';
import OpenDrawerButton from '@/components/drawers/open-drawer-button';
import { QueryKey } from '@/lib/query-client';
import DomSize from '@/components/dom-size';
import Chip from '@/components/chip';
import { DIRECTORY_SYNC_LABEL } from '@/components/drawers/admin-user/helpers';
import PageBlurb from '@/components/page/blurb';
import PageTools from '@/components/page/tools';
import useDrawer from '@/hooks/use-drawer.hook';
import useFeature from '@/hooks/use-feature.hook';
import Tooltip from '@/components/tooltip';

const columns: DtColumn[] = [
  { name: 'name', label: 'Name', flex: 2, sortable: true },
  { name: 'email', label: 'Email', flex: 3, sortable: true },
  { name: 'role', label: 'Role', sortable: true },
  { name: 'action', label: '', sortable: false },
];

const AdminUsersPage: FC<PageProps> = () => {
  const { openDrawer } = useDrawer();
  const { canManageAdmins } = useFeature();

  const handleView = (userId?: string) => {
    openDrawer('admin-user', {
      id: userId || null,
    });
  };

  const loadData = async (pageParams: DtPage, filter?: DtFilter) => {
    const { page, pageSize, sort } = pageParams;
    const dataset = await getAdminUsers(page, pageSize, sort, filter);

    return {
      ...dataset,
      rows: dataset.rows.map(({ id, name, email, role, isDirectorySync }) => {
        return {
          id,
          name: (
            <Stack direction="row" gap={1} alignItems="center" maxWidth="100%">
              {name}
              {isDirectorySync && <Chip label={DIRECTORY_SYNC_LABEL} size="small" />}
            </Stack>
          ),
          email,
          role: <Text size="small">{AdminRoleLabels[role]}</Text>,
          action: <OpenDrawerButton onClick={() => handleView(id)} />,
        };
      }),
    };
  };

  return (
    <Page title="Admin Users">
      <PageHeader>
        <PageBlurb>
          View admin users and their roles. Only organization owners can add, edit, or delete admin
          users.
        </PageBlurb>
        <PageTools>
          <Tooltip
            title="You must be an organization owner to manage admin users."
            disabled={canManageAdmins}
          >
            <Button
              label="Add Admin User"
              icon="plus"
              onClick={() => handleView()}
              disabled={!canManageAdmins}
            />
          </Tooltip>
        </PageTools>
      </PageHeader>
      <DomSize>
        <DataTable
          queryKey={QueryKey.AdminUsersDataset}
          columns={columns}
          onLoad={loadData}
          search
          searchPlaceholder="Search by name or email"
          sort={{ columnName: 'name', direction: 'asc' }}
        />
      </DomSize>
    </Page>
  );
};

export default AdminUsersPage;
